import React, { useMemo } from 'react';

interface RankedListItem {
    name?: string;
    competencyTitle?: string;
    title?: string;
    label?: string;
    rating?: number | null;
    score?: number | null;
    value?: number | null;
    rank?: number;
    [key: string]: unknown;
}

interface RankedListProps {
    data: Array<Record<string, unknown>>;
    options?: {
        show_rating?: boolean;
        show_rank?: boolean;
        max_items?: number;
        highlight_low?: boolean;
    };
}

// Helper to get display name from normalized item
const getItemName = (item: RankedListItem): string => {
    return item.name
        || item.competencyTitle
        || item.title
        || item.label
        || 'Unknown';
};

// Helper to get rating/score from normalized item (handles null values)
const getItemRating = (item: RankedListItem): number | null => {
    const value = item.rating ?? item.score ?? item.value;
    return value !== undefined && value !== null ? Number(value) : null;
};

export const RankedList: React.FC<RankedListProps> = ({ data, options = {} }) => {
    const {
        show_rating = true,
        show_rank = true,
        max_items = 5,
        highlight_low = false
    } = options;

    const items = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        return data.slice(0, max_items) as RankedListItem[];
    }, [data, max_items]);

    if (items.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400 text-sm">No data available</div>;
    }

    return (
        <div className="flex flex-col h-full overflow-auto p-2">
            {items.map((item, index) => {
                const rating = getItemRating(item);
                const name = getItemName(item);
                const hasRating = rating !== null;
                const ratingValue = rating ?? 0;
                const isLow = highlight_low && hasRating && ratingValue < 3;
                const rank = item.rank || index + 1;

                return (
                    <div
                        key={name + index}
                        className={`flex items-center justify-between p-3 mb-2 rounded-lg transition-colors ${isLow ? 'bg-red-50 border border-red-100' : 'bg-white border border-gray-100 hover:bg-gray-50'
                            }`}
                    >
                        <div className="flex items-center gap-3 overflow-hidden">
                            {show_rank && (
                                <div className={`
                    flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                    ${rank <= 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}
                `}>
                                    #{rank}
                                </div>
                            )}
                            <span className="text-sm font-medium text-gray-900 truncate" title={name}>
                                {name}
                            </span>
                        </div>

                        {show_rating && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {hasRating ? (
                                    <>
                                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${ratingValue >= 4 ? 'bg-green-500' :
                                                        ratingValue >= 3 ? 'bg-blue-500' :
                                                            'bg-red-500'
                                                    }`}
                                                style={{ width: `${(ratingValue / 5) * 100}%` }}
                                            />
                                        </div>
                                        <span className="text-xs font-bold text-gray-700 w-6 text-right">
                                            {ratingValue.toFixed(1)}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-xs text-gray-400 italic">
                                        No rating
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
